import { useState, useEffect, useRef, useContext } from 'react'
import { isEmpty, isEqual } from 'lodash'
import { useDispatch } from 'react-redux'
import { useDeepCompareEffect } from 'react-use'
import PropTypes from 'prop-types'
import {
  DatasetDataContext,
  ActionType as DatasetDataActionType,
} from '@datasets/dataset-details/datasetData/context'
import { showSnackbar } from 'functions'
import { ReadGridToolbarContext } from './context/toolbar'
import { selectionDataToCopy } from './utils'
import { t } from '@lingui/core/macro'

const SNACKBAR_TYPES = {
  ERROR: 'ERROR',
  INFO: 'INFO',
  SUCCESS: 'SUCCESS',
}

const { SUCCESS } = SNACKBAR_TYPES

export const Selector = props => {
  const {
    children,
    columnCount,
    getDataForSelection,
    rerender,
    rowCount,
    scrollToColumn,
    scrollToRow,
  } = props
  const dispatch = useDispatch()
  const [activeCell, setActiveCell] = useState([])
  const [clickedStartCell, setClickedStartCell] = useState(null)
  const [lastArrowKeyPressed, setLastArrowKeyPressed] = useState(null)
  const [mouseSelecting, setMouseSelecting] = useState(false)
  const [node, setNode] = useState(document.getElementById('copyText'))
  const [rangeStart, setRangeStart] = useState([])
  const [selectEntireDataset, setSelectEntireDataset] = useState(false)
  const gridScrollTargetRef = useRef(null)
  const { gridRef } = useContext(ReadGridToolbarContext)
  const {
    dispatch: dispatchDatasetData,
    selectedCell,
    selectedCells,
    selectedRows,
  } = useContext(DatasetDataContext)

  useDeepCompareEffect(() => {
    if (isEmpty(selectedRows)) return

    dispatchDatasetData({ type: DatasetDataActionType.setSelectedCells, payload: {} })
  }, [selectedRows])

  useEffect(() => {
    window.addEventListener('copy', handleCopy)
    window.addEventListener('keydown', handleUpdateArrowKeyPressed)
    return () => {
      window.removeEventListener('copy', handleCopy)
      window.removeEventListener('keydown', handleUpdateArrowKeyPressed)
    }
  }, [selectedCells])

  useEffect(() => {
    if (!selectedCell) return

    selectCell()

    function selectCell() {
      const newSelected = { [selectedCell]: true }
      rerender(newSelected)
      // TODO: How does this work with hitting enter on each, warning to copy vs wanting to hit enter again? Maybe we don't selectText via this route and it won't copy?
    }
  }, [selectedCell])

  /**
   * Copy event on read grid; get data from selection and convert to a string.
   * Set copied data to the clipboard event.
   * @param {*} e
   */
  async function handleCopy(e) {
    e.preventDefault()

    const selectionData = await getDataForSelection(selectedCells, selectEntireDataset)

    const selectionText = selectionDataToCopy({ data: selectionData })

    await navigator.clipboard.writeText(selectionText.toString())

    dispatch(
      showSnackbar({
        message: t`Copied!`,
        type: SUCCESS,
        duration: 3000,
      }),
    )
  }

  function handleUpdateArrowKeyPressed(e) {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    if (e.shiftKey && e.key.includes('Arrow')) {
      setLastArrowKeyPressed(e.key)
    }
  }

  function handleKeyDown(e) {
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
      case 'ArrowRight':
      case 'ArrowDown': {
        handleArrowKeyDown(e)
        break
      }
      case 'Shift': {
        handleShiftKeyDown()
        break
      }
      case 'a': {
        handleCmdCtrlAKeyDown(e)
        break
      }
      case 'Escape': {
        dispatchDatasetData({ type: DatasetDataActionType.setSelectedCells, payload: {} })
        if (selectEntireDataset) setSelectEntireDataset(false)
        break
      }
      default: {
        if (selectEntireDataset) setSelectEntireDataset(false)
        break
      }
    }
  }

  function handleKeyUp(e) {
    if (e.key === 'Shift') {
      setActiveCell(rangeStart)
      setRangeStart([])
      setLastArrowKeyPressed(null)
    }
  }

  function handleShiftKeyDown() {
    const { min, max } = getRangeMinMax()
    setRangeStart(min)
    setActiveCell(max)
    setLastArrowKeyPressed(null)
  }

  function handleArrowKeyDown(e) {
    const nextCell = getNextCell(e)
    if (isEqual(nextCell, activeCell)) return
    const [nextCellRow, nextCellCol] = nextCell
    const nextCellKey = buildKey(nextCellRow, nextCellCol)
    const directionChanged = checkIfDirectionChanged(e.key)

    if (e.shiftKey) {
      if (isEmpty(rangeStart)) {
        setRangeStart(activeCell)
      }

      let updatedRange = {}
      const [rangeStartRow, rangeStartCol] = rangeStart
      const nextCellOnNewRow = nextCellRow !== rangeStartRow
      const nextCellOnNewCol = nextCellCol !== rangeStartCol
      const expandSelection = !isEmpty(rangeStart) && nextCellOnNewRow && nextCellOnNewCol

      if (expandSelection) {
        updatedRange = selectAllCellsWithinBounds({
          row: nextCellRow,
          col: nextCellCol,
          fixedStartCell: rangeStart,
        })
      } else {
        updatedRange = addOrRemoveCellsFromRange({ directionChanged, nextCellRow, nextCellCol })
      }
      dispatchDatasetData({ type: DatasetDataActionType.setSelectedCells, payload: updatedRange })
    } else {
      dispatchDatasetData({
        type: DatasetDataActionType.setSelectedCells,
        payload: { [nextCellKey]: true },
      })
    }
    setActiveCell(nextCell)
  }

  function handleCmdCtrlAKeyDown(e) {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault()
      const range = selectAllCellsWithinBounds({
        row: rowCount,
        col: columnCount,
        fixedStartCell: [1, 1],
      })
      dispatchDatasetData({ type: DatasetDataActionType.setSelectedCells, payload: range })
      setSelectEntireDataset(true)
    }
  }

  function getNextCell(e) {
    const readGridCell = e.target.querySelector('.read-grid-cell')
    let [dataRow, dataCol] = (() => {
      if (!isEmpty(activeCell)) {
        return activeCell
      }

      return [+readGridCell?.getAttribute('data-row'), +readGridCell?.getAttribute('data-column')]
    })()

    if (!isEmpty(activeCell)) {
      switch (e.key) {
        case 'ArrowLeft':
          if (dataCol !== 1) {
            dataCol -= 1
          }
          break
        case 'ArrowUp':
          if (dataRow !== 1) {
            dataRow -= 1
          }
          break
        case 'ArrowRight':
          if (dataCol !== columnCount) {
            dataCol += 1
          }
          break
        case 'ArrowDown':
          if (dataRow !== rowCount) {
            dataRow += 1
          }
          break
        default:
          break
      }
    }

    const nextCell = e.target.querySelector(`[data-column="${dataCol}"][data-row="${dataRow}"]`)

    if (nextCell) {
      e.preventDefault() // prevent scrolling
      scrollIntoViewIfNeeded(nextCell)
    }

    return [dataRow, dataCol]
  }

  /**
   * Determine if the element is in viewport, if it's not scroll it into view.
   * If the element is above the viewport then we walk backwards, if it's below then
   * we step forward.
   * @param {*} el DOM Node
   */
  function scrollIntoViewIfNeeded(el) {
    const elRect = el?.getBoundingClientRect()
    if (!elRect) return

    const contRect = document.querySelector('.rendered')?.getBoundingClientRect()
    if (!contRect) return

    const elIsBelowContent = elRect.top < contRect.height + contRect.top
    const EL_PADDING = 50
    const elIsAboveContent = elRect.top <= contRect.top + EL_PADDING

    if (
      elIsBelowContent ||
      elRect.left < contRect.left ||
      elRect.bottom < contRect.top ||
      elRect.right > contRect.right
    ) {
      const rowNum = +el.dataset.row
      const walkRowBackStopAtOne = rowNum > 2 ? rowNum - 2 : 1
      const row = elIsAboveContent ? walkRowBackStopAtOne : +el.dataset.row
      const column = +el.dataset.column

      if (row && column) {
        if (row === 1) {
          // ScrollToCell does not move grid to reveal first row; alternatively used scrollToPosition.
          gridRef?.current?._bottomRightGrid?.scrollToPosition({ scrollTop: 0 })
        } else {
          gridRef?.current?._bottomRightGrid?.scrollToCell({ columnIndex: column, rowIndex: row })
        }
      }
    }
  }

  const buildKey = (row, col) => `${row}_${col}`

  function checkIfDirectionChanged(key) {
    switch (key) {
      case 'ArrowUp': {
        return lastArrowKeyPressed === 'ArrowDown'
      }
      case 'ArrowDown': {
        return lastArrowKeyPressed === 'ArrowUp'
      }
      case 'ArrowRight': {
        return lastArrowKeyPressed === 'ArrowLeft'
      }
      case 'ArrowLeft': {
        return lastArrowKeyPressed === 'ArrowRight'
      }
      default:
        return false
    }
  }

  function selectAllCellsWithinBounds({ row, col, fixedStartCell }) {
    const result = {}
    const rows = [row]
    const cols = [col]

    if (fixedStartCell) {
      rows.push(fixedStartCell[0])
      cols.push(fixedStartCell[1])
    }

    let startRow = Math.min(...rows)
    let endRow = Math.max(...rows)
    let startCol = Math.min(...cols)
    let endCol = Math.max(...cols)

    while (startRow <= endRow && startCol <= endCol) {
      // Select cells in the first row
      for (let col = startCol; col <= endCol; col++) {
        result[`${startRow}_${col}`] = true
      }

      // Select cells in the last column
      for (let row = startRow + 1; row <= endRow; row++) {
        result[`${row}_${endCol}`] = true
      }

      // Select cells in the last row
      for (let col = endCol - 1; col >= startCol; col--) {
        if (startRow === endRow) break
        result[`${endRow}_${col}`] = true
      }

      // Select cells in the first column
      for (let row = endRow - 1; row > startRow; row--) {
        if (startCol === endCol) break
        result[`${row}_${startCol}`] = true
      }

      startRow++
      endRow--
      startCol++
      endCol--
    }

    return result
  }

  function addOrRemoveCellsFromRange({ directionChanged, nextCellRow, nextCellCol }) {
    const nextCellKey = buildKey(nextCellRow, nextCellCol)
    const activeCellKey = buildKey(activeCell[0], activeCell[1])
    const removeCellFromRange = nextCellKey in selectedCells
    let updatedRange = {}

    if (directionChanged) {
      return selectedCells
    }

    if (!isEmpty(rangeStart)) {
      updatedRange = selectAllCellsWithinBounds({
        row: nextCellRow,
        col: nextCellCol,
        fixedStartCell: rangeStart,
      })

      return updatedRange
    }

    updatedRange = {
      ...selectedCells,
      [nextCellKey]: true,
    }

    if (removeCellFromRange) {
      delete updatedRange[activeCellKey]
    }

    return updatedRange
  }

  function getRangeMinMax() {
    let minRow = Infinity
    let maxRow = -Infinity
    let minCell = []
    let maxCell = []
    const cellKeys = Object.keys(selectedCells)

    cellKeys.forEach(key => {
      const [row, col] = key.split('_')
      if (row < minRow) {
        minRow = +row
        minCell = [minRow, +col]
      }
      if (row > maxRow) {
        maxRow = +row
        maxCell = [maxRow, +col]
      }
    })

    return {
      min: minCell,
      max: maxCell,
    }
  }

  function _onSectionRendered() {}

  function isClickable() {}

  function handleMouseDown(e) {
    const clickedOnScrollBar = e.target?.getAttribute('id') === 'read-grid'
    const rightClick = e.button === 2

    if (clickedOnScrollBar) return

    setLastArrowKeyPressed(null)

    // Preserve right click; don't select a single cell.
    // Allows for a context menu on a group of cells.
    if (rightClick) return

    if (!isEmpty(selectedRows)) return

    if (!node) {
      setNode(document.getElementById('copyText'))
    }
    if (e.target.closest('.read-grid-cell')) {
      e.target = e.target.closest('.read-grid-cell')
    }

    const clickedCellCol = +e.target?.getAttribute('data-column')
    const clickedCellRow = +e.target?.getAttribute('data-row')

    let updatedRange = {}
    if (e.shiftKey) {
      setActiveCell([clickedCellRow, clickedCellCol])
      updatedRange = selectAllCellsWithinBounds({
        row: clickedCellRow,
        col: clickedCellCol,
        fixedStartCell: rangeStart,
      })
    } else {
      updatedRange[`${clickedCellRow}_${clickedCellCol}`] = true
      setActiveCell([clickedCellRow, clickedCellCol])
      setMouseSelecting(true)
      setClickedStartCell({ row: clickedCellRow, col: clickedCellCol })
    }

    dispatchDatasetData({ type: DatasetDataActionType.setSelectedCells, payload: updatedRange })
  }

  function handleMouseUp() {
    setMouseSelecting(false)
  }

  /**
   * On mouse move we need to determine if we are drag selecting rows;
   * scroll the cells outside the viewport into view;
   * set newly selected cells and rerender those cells as selected.
   * @param {*} e Mouse move event.
   */
  function handleMouseMove(e) {
    if (!mouseSelecting) {
      return
    }

    if (!e.target.classList.contains('read-grid-cell-data')) {
      const findClostestDataCell = e.target.closest('.read-grid-cell-data')
      // check to see if there is a valid target in the dom
      if (findClostestDataCell) {
        // Set target and ref; ensure we have a stable reference of the last known cell.
        e.target = findClostestDataCell
        gridScrollTargetRef.current = e.target
      } else if (gridScrollTargetRef.current) {
        // If no data cell is found, used last known cell.
        // IF currrent target is not the same as our last reference; we know we need to walk back to the previous node.
        if (!e.target.isEqualNode(gridScrollTargetRef.current)) {
          const currentRow = gridScrollTargetRef.current.dataset.row
          if (+currentRow === 1) return // Reached the first row; STOP.

          // Set previous row as the next row we are scrolling to.
          const $prevRow = gridScrollTargetRef.current.previousSibling
          e.target = $prevRow
          gridScrollTargetRef.current = $prevRow
        }
      }
    } else {
      // Data cell is found, set as reference for next event.
      gridScrollTargetRef.current = e.target
    }

    const newSelected = {}
    const col = +e.target?.getAttribute('data-column')
    const row = +e.target?.getAttribute('data-row')

    scrollIntoViewIfNeeded(e.target)

    // the table references 0_0 if the user has
    // right clicked on a cell, but haven't necessarily selected it
    // returning here before it runs all weird diffs
    if (selectedCells['0_0']) return

    // do some math between start cell and end row.
    const colDiff = Math.abs(clickedStartCell.col - col)
    const colIncrease = clickedStartCell.col - col < 0 ? 1 : -1
    const rowDiff = Math.abs(clickedStartCell.row - row)
    const rowIncrease = clickedStartCell.row - row < 0 ? 1 : -1

    for (let c = 0; c <= colDiff; c++) {
      for (let r = 0; r <= rowDiff; r++) {
        newSelected[
          `${clickedStartCell.row + r * rowIncrease}_${clickedStartCell.col + c * colIncrease}`
        ] = true
      }
    }

    newSelected[`${row}_${col}`] = true
    dispatchDatasetData({ type: DatasetDataActionType.setSelectedCells, payload: newSelected })
  }

  return (
    <div
      className="rendered"
      style={{ height: '100%', width: '100%' }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onCopy={handleCopy}
    >
      {children({
        onSectionRendered: _onSectionRendered,
        scrollToColumn,
        scrollToRow,
        isClickable,
      })}
    </div>
  )
}

Selector.propTypes = {
  children: PropTypes.func.isRequired,
  columnCount: PropTypes.number.isRequired,
  getDataForSelection: PropTypes.func.isRequired,
  rerender: PropTypes.func.isRequired,
  rowCount: PropTypes.number.isRequired,
  scrollToColumn: PropTypes.func.isRequired,
  scrollToRow: PropTypes.func.isRequired,
}
